import { useEffect, useState, type JSX, type ReactNode } from 'react';

interface ModalProps {
  open: boolean;
  onClose?: () => void;
  children: ReactNode;
  className?: string;
  title?: string;
  classNameTitle?: string;
  preventCloseClickOut?: boolean;
}

export const Modal = ({
  open,
  onClose,
  children,
  className = '',
  title = '',
  classNameTitle = '',
  preventCloseClickOut = false,
}: ModalProps): JSX.Element | null =>
{
  const [isVisible, setIsVisible] = useState<boolean>(open);
  const [closing, setClosing] = useState(false);
  useEffect(() =>
  {
    if (open)
    {
      setIsVisible(true);
      setClosing(false);
      return;
    }
    else
    {
      setClosing(true);
      const timeout = setTimeout(() =>
      {
        setClosing(false);
        setIsVisible(false);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [open, isVisible]);

  useEffect(() =>
  {
    if (!open) return () => {};
    const handleKeyDown = (e: KeyboardEvent): void =>
    {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () =>
    {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  if (!isVisible) return null;

  return (
    <div
      tabIndex={1}
      className={`fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto transition-opacity cursor-default duration-300 ${!closing ? 'opacity-100' : 'opacity-0'} ${preventCloseClickOut ? 'pointer-events-none' : ''}`}
    >
      {/* Overlay */}
      {!preventCloseClickOut && (
        <div className="absolute inset-0" onClick={() => onClose?.()} />
      )}

      {/* Modal */}
      <div
        className={`relative z-50 w-fit h-fit max-w-[90vw] max-h-[90vh] bg-white dark:bg-[#333535] dark:border-[#4a4c4c] p-6 border shadow-dialog transform transition-all duration-300 ease-in-out pointer-events-auto ${!closing ? 'scale-100 opacity-100' : 'scale-85 opacity-0'} ${className}`}
      >
        <form className="w-full h-min-[21px]">
          <div className="h-7">
            <div
              className="icon-incorrect-icon text-brightGray dark:!text-[#6e6860] dark:hover:!text-[#a9a093] text-[14px] absolute right-[18px] top-5 cursor-pointer p-2"
              onClick={() => onClose?.()}
            />
            <div className="flex align-middle justify-start w-full">
              <p
                className={`static text-xl text-[#4887be] text-[21px] dark:!text-[#62aacb] leading-[21px] font-sans font-medium ${classNameTitle}`}
              >
                {title}
              </p>
            </div>
          </div>
          {children}
        </form>
      </div>
    </div>
  );
};
